import { Fragment } from "react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SPACING_811_HOA_DISCLAIMER,
  SPACING_811_HOA_ITEMS,
} from "@/lib/disclaimers";

// Render the canonical disclaimer string with any "call811.com" mentions turned
// into a new-tab link. The constant stays plain text (the email template renders
// it as a string), so the linkifying lives here in the UI component.
function withCall811Link(text: string) {
  const TOKEN = "call811.com";
  return text.split(TOKEN).map((part, i, arr) => (
    <Fragment key={i}>
      {part}
      {i < arr.length - 1 && (
        <a
          href="https://call811.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 hover:text-plantista-700 dark:hover:text-plantista-300"
        >
          {TOKEN}
        </a>
      )}
    </Fragment>
  ));
}

/**
 * Horticultural + legal notes (PLA2-371): spacing/suitability + 811
 * (call-before-you-dig) + HOA. Shown on designs and checkout (the design email
 * surfaces the same copy via `SPACING_811_HOA_DISCLAIMER` in `@/lib/disclaimers`).
 *
 * Always expanded (PLA2-765): collapsed, the summary line read like an error
 * code and users didn't realize it was clickable.
 */
export { SPACING_811_HOA_DISCLAIMER };

export function HorticulturalDisclaimer({
  className,
  items = SPACING_811_HOA_ITEMS,
}: {
  className?: string;
  /** Override the notes (the container flow passes CONTAINER_PLANTING_ITEMS). */
  items?: readonly string[];
}) {
  return (
    <section
      aria-label="Before you plant"
      className={cn(
        "rounded-lg border border-plantista-200 bg-plantista-50/60 px-3 py-2 text-xs text-plantista-600 dark:border-plantista-800 dark:bg-plantista-900/40 dark:text-plantista-400",
        className
      )}
    >
      <p className="flex items-center gap-1.5 font-bold text-plantista-800 dark:text-plantista-200">
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Before you plant
      </p>
      <ul className="mt-1.5 list-disc space-y-1 pl-5 leading-relaxed">
        {items.map((item) => (
          <li key={item}>{withCall811Link(item)}</li>
        ))}
      </ul>
    </section>
  );
}
